import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import MessageList from './MessageList'
import ChatInput from './ChatInput'
import ProviderSelector from './ProviderSelector'
import FormattedContent from './FormattedContent'
import useStreaming from '../hooks/useStreaming'

function ChatContainer({ conversationId: initialConversationId, initialMessages, providers, currentProvider: initialProvider, compact, context, originPage }) {
  const [conversationId, setConversationId] = useState(initialConversationId)
  const [currentProvider, setCurrentProvider] = useState(initialProvider)
  const [messages, setMessages] = useState(initialMessages || [])
  const [isLoading, setIsLoading] = useState(false)
  const [streamingContent, setStreamingContent] = useState('')
  const [toolStatus, setToolStatus] = useState(null)
  const [contextAttached, setContextAttached] = useState(!!context)
  const messagesEndRef = useRef(null)
  const streamingContentRef = useRef('')

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    if (messages.length > 0 || streamingContent) {
      scrollToBottom()
    }
  }, [messages, streamingContent])

  const handleStreamDelta = (content) => {
    setToolStatus(null)
    streamingContentRef.current += content
    setStreamingContent(prev => prev + content)
  }

  const handleToolStatus = () => {
    setToolStatus('Querying your data…')
  }

  const handleStreamComplete = (data) => {
    // Use content from server response, or fall back to accumulated ref content
    const finalContent = data?.content || streamingContentRef.current

    // Add the complete message to the list; the server sends the persisted
    // message id, which export links need
    if (finalContent) {
      setMessages(prev => [...prev, {
        id: data?.message_id || Date.now(),
        role: 'assistant',
        content: finalContent,
        created_at: new Date().toISOString(),
        persisted: !!data?.message_id
      }])
    }
    streamingContentRef.current = ''
    setStreamingContent('')
    setToolStatus(null)
    setIsLoading(false)
  }

  const handleError = (message) => {
    console.error('Chat error:', message)
    setMessages(prev => [...prev, {
      id: Date.now(),
      role: 'assistant',
      content: `Error: ${message}`,
      created_at: new Date().toISOString(),
      isError: true,
      persisted: false
    }])
    streamingContentRef.current = ''
    setStreamingContent('')
    setToolStatus(null)
    setIsLoading(false)
  }

  const { sendMessage } = useStreaming(conversationId, {
    onStreamDelta: handleStreamDelta,
    onStreamComplete: handleStreamComplete,
    onToolStatus: handleToolStatus,
    onError: handleError
  })

  const createConversation = async (content) => {
    const csrfToken = document.querySelector('[name="csrf-token"]')?.content

    try {
      const response = await fetch('/ai_conversations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken || ''
        },
        body: JSON.stringify({
          ai_conversation: {
            ai_provider: currentProvider,
            origin_page: originPage,
            ...(contextAttached && context
              ? { context_type: context.type, context_id: context.id }
              : {})
          },
          initial_message: content
        })
      })

      if (!response.ok) {
        throw new Error('Failed to create conversation')
      }

      const data = await response.json()

      // Update URL without reload
      window.history.pushState({}, '', `/ai_conversations/${data.conversation_id}`)

      return data.conversation_id
    } catch (error) {
      console.error('Error creating conversation:', error)
      throw error
    }
  }

  const handleSend = async (content) => {
    if (!content.trim() || isLoading) return

    // Add user message immediately
    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: content.trim(),
      created_at: new Date().toISOString()
    }
    setMessages(prev => [...prev, userMessage])
    setIsLoading(true)
    streamingContentRef.current = ''
    setStreamingContent('')

    // If no conversation yet, create one first
    if (!conversationId) {
      try {
        const newConversationId = await createConversation(content.trim())
        setConversationId(newConversationId)
        // The server will start processing the message, ActionCable will connect with new ID
      } catch (error) {
        handleError('Failed to start conversation. Please try again.')
      }
    } else {
      // Send via ActionCable
      sendMessage(content.trim())
    }
  }

  const handleProviderChange = (newProvider) => {
    setCurrentProvider(newProvider)
  }

  const contextSuggestions = {
    Job: ['Summarize this job', "What's still outstanding?", 'Have any results failed?', 'What can I do on this page?'],
    Task: ['Tell me about these results', 'Have any results failed?', "What's left to test on this task?", 'What can I do on this page?']
  }
  const suggestions = contextAttached && context
    ? (contextSuggestions[context.type] || contextSuggestions.Job)
    : originPage
      ? ['How many jobs are in progress?', 'What can I do on this page?', 'How do I create a job?']
      : null

  const contextRemovable = messages.length === 0 && !conversationId && !isLoading

  return (
    <div className={compact ? 'ai-chat-container ai-chat-compact' : 'ai-chat-container'}>
      {compact ? (
        <div className="ai-chat-header ai-chat-header-compact">
          <a href={conversationId ? `/ai_conversations/${conversationId}` : '/ai_conversations/new'}>
            Open full view ↗
          </a>
        </div>
      ) : (
        <div className="ai-chat-header">
          {providers && providers.length > 0 ? (
            <ProviderSelector
              providers={providers}
              currentProvider={currentProvider}
              conversationId={conversationId}
              onProviderChange={handleProviderChange}
              locked={messages.length > 0}
            />
          ) : (
            <span className="text-sm text-gray-500">No AI providers configured</span>
          )}
        </div>
      )}

      <div className="ai-chat-messages">
        <MessageList messages={messages} suggestions={suggestions} onSuggestion={handleSend} conversationId={conversationId} />
        {streamingContent && (
          <div className="ai-message ai-message-assistant ai-message-streaming">
            <div className="ai-message-content">
              <FormattedContent content={streamingContent} />
              {toolStatus && (
                <div><span className="ai-loading-indicator">{toolStatus}</span></div>
              )}
            </div>
          </div>
        )}
        {isLoading && !streamingContent && (
          <div className="ai-message ai-message-assistant ai-message-loading">
            <div className="ai-message-content">
              <span className="ai-loading-indicator">{toolStatus || 'Thinking...'}</span>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {contextAttached && context && (
        <div className="ai-context-bar">
          <span className="ai-context-chip">
            <span className="ai-context-chip-label">📋 {context.label}</span>
            {contextRemovable && (
              <button
                type="button"
                className="ai-context-chip-remove"
                onClick={() => setContextAttached(false)}
                aria-label="Remove context"
              >
                ✕
              </button>
            )}
          </span>
        </div>
      )}

      <ChatInput onSend={handleSend} disabled={isLoading || !providers || providers.length === 0} />
    </div>
  )
}

ChatContainer.propTypes = {
  conversationId: PropTypes.number,
  initialMessages: PropTypes.array,
  providers: PropTypes.array,
  currentProvider: PropTypes.string,
  compact: PropTypes.bool,
  context: PropTypes.shape({
    type: PropTypes.string,
    id: PropTypes.number,
    label: PropTypes.string
  }),
  originPage: PropTypes.string
}

export default ChatContainer
